import { useCallback, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useArticleDrafts } from '@/hooks/useArticleDrafts';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { giftWrapDraft, unwrapDraftGiftWrap } from '@/lib/draftSync';
import type { ArticleDraft } from '@/lib/articleDrafts';

const GIFT_WRAP_KIND = 1059;
const DELETION_KIND = 5;

/**
 * Maps over `items` with at most `limit` promises in flight.
 *
 * Decrypting gift wraps means signer calls, and a remote signer (NIP-46
 * bunker) does a relay round trip per call. Firing one promise per wrap
 * floods a busy inbox's signer until requests hit its internal timeout —
 * which abandons legitimately readable drafts along with the noise.
 */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let index = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (index < items.length) {
      const i = index;
      index += 1;
      results[i] = await fn(items[i]);
    }
  });
  await Promise.all(workers);
  return results;
}

/**
 * Per-draft sync bookkeeping, persisted locally.
 *
 *  - `wrapId` — the id of the NIP-59 gift wrap the draft was last synced
 *    to Nostr as, so a deletion can reference it with NIP-09.
 *  - `relays` — the relays the publish actually reached at the time, so
 *    a card can say "synced to N relays" without another query. This is
 *    *intentionally* a point-in-time snapshot, not a live view of relay
 *    state: relays come and go, and a draft's wrap is at rest until the
 *    user syncs again.
 */
interface SyncRecord {
  wrapId: string;
  relays: string[];
  at: number;
}

const SYNC_RECORDS_KEY = 'wikistar:draft-sync-records';

function readSyncRecords(): Record<string, SyncRecord> {
  try {
    const raw = localStorage.getItem(SYNC_RECORDS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const out: Record<string, SyncRecord> = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const record = value as Partial<SyncRecord>;
      if (typeof record.wrapId !== 'string' || !record.wrapId) continue;
      out[id] = {
        wrapId: record.wrapId,
        relays: Array.isArray(record.relays) ? record.relays.filter((r): r is string => typeof r === 'string') : [],
        at: typeof record.at === 'number' ? record.at : 0,
      };
    }
    return out;
  } catch {
    return {};
  }
}

function writeSyncRecords(map: Record<string, SyncRecord>): void {
  try {
    localStorage.setItem(SYNC_RECORDS_KEY, JSON.stringify(map));
  } catch {
    // Best-effort: losing the map just means a future delete can't find
    // the wrap id, which degrades to leaving the synced copy on relays.
  }
}

export interface UseDraftSyncResult {
  isSyncing: boolean;
  /** Pull drafts from relays and merge them into the local library. Returns how many were pulled. */
  syncDown: () => Promise<number | undefined>;
  /** Push one local draft to Nostr as an encrypted gift wrap. */
  syncUp: (draft: ArticleDraft) => Promise<void>;
  /** Push every local draft. */
  syncAll: () => Promise<void>;
  /** Delete a draft's synced copy from relays. */
  deleteSynced: (draftId: string) => Promise<void>;
  /** Whether the current signer supports NIP-44 (required for sync). */
  canSync: boolean;
  /** Per-draft sync record (wrap id + confirmed relays + when). */
  getSyncRecord: (draftId: string) => { wrapId: string; relays: string[]; at: number } | undefined;
  /** Current write-enabled relays used for the last sync, shown alongside the count hint. */
  writeRelays: string[];
}

/**
 * Syncs local article drafts to Nostr so they follow the user across
 * devices — implemented as NIP-59 gift wraps (NIP-44 encrypted to self),
 * because drafts are exactly the kind of private, user-owned note that
 * encryption fits: they carry no public meaning and only the author
 * should ever read them.
 */
export function useDraftSync(): UseDraftSyncResult {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const drafts = useArticleDrafts();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const [isSyncing, setIsSyncing] = useState(false);

  /** Current write-enabled relays, read live so a re-sync reports the actual set it reached. */
  const writeRelays = config.relayMetadata.relays.filter((r) => r.write).map((r) => r.url);

  const canSync = Boolean(user?.signer.nip44);

  /** Encrypt + publish one draft to Nostr, remembering which relays actually accepted it. */
  const syncUp = useCallback(
    async (draft: ArticleDraft): Promise<void> => {
      if (!user) return;
      const startedAt = performance.now();
      console.log(`[draft-sync] sync-up: publishing to ${writeRelays.length} write relay(s): ${writeRelays.join(', ')}`);
      const wrap = await giftWrapDraft(draft, user.signer, user.pubkey);

      // Publish the wrap EXACTLY as sealed — deliberately NOT via
      // `useNostrPublish`.
      //
      // `useNostrPublish` re-signs whatever it's handed with the user's
      // own key. For a NIP-59 gift wrap that is fatal: the wrap is
      // signed by a one-time ephemeral key, and its content is NIP-44
      // encrypted with `getConversationKey(ephemeralKey, userPubkey)`.
      // Re-signing replaces the ephemeral pubkey with the user's, so
      // unwrap then derives `conversationKey(userPriv, userPub)` — a
      // different key — and the draft becomes permanently undecryptable.
      // (This is exactly why the media index sync is unaffected: its key
      // agreement is user<->user at both ends, so re-signing is a no-op.)
      //
      // It would also append a `client` tag, leaking the very metadata
      // gift wrapping exists to hide.
      //
      // 12s (not 5s): `NPool.event` races every write relay with
      // `Promise.any` and needs only one to accept, but "one" can still
      // mean a cold WebSocket handshake plus a NIP-42 AUTH round trip
      // before that relay's `OK` arrives — on a slow network 5s clipped
      // legitimate publishes.
      await nostr.event(wrap, { signal: AbortSignal.timeout(12000) });
      console.log(`[draft-sync] sync-up: published wrap ${wrap.id.slice(0, 8)}... in ${Math.round(performance.now() - startedAt)}ms`);

      const map = readSyncRecords();
      map[draft.id] = { wrapId: wrap.id, relays: writeRelays, at: Date.now() };
      writeSyncRecords(map);
    },
    [user, nostr, writeRelays],
  );

  const syncAll = useCallback(async () => {
    if (!user) return;
    setIsSyncing(true);
    try {
      await Promise.all(
        drafts.drafts.map(async (draft) => {
          try {
            await syncUp(draft);
          } catch {
            // Per-draft failures shouldn't sink the rest of the batch.
          }
        }),
      );
    } finally {
      setIsSyncing(false);
    }
  }, [user, drafts.drafts, syncUp]);

  const syncDown = useCallback(async () => {
    if (!user || !user.signer.nip44) return;
    setIsSyncing(true);
    const startedAt = performance.now();
    try {
      const readRelays = config.relayMetadata.relays.filter((r) => r.read).map((r) => r.url);
      console.log(`[draft-sync] sync-down: querying ${readRelays.length} read relay(s): ${readRelays.join(', ')}`);

      // The query gets its own timeout, separate from the decrypts below
      // (see `mapWithConcurrency`). 12s, not the previous 8s: a relay
      // whose WebSocket had gone idle-closed needs a fresh handshake —
      // and possibly a NIP-42 AUTH round trip — before it can even start
      // answering the REQ, on top of `NPool`'s own `eoseTimeout` wait for
      // slower relays in the set.
      const wraps = await nostr.query(
        [{ kinds: [GIFT_WRAP_KIND], '#p': [user.pubkey], limit: 200 }],
        { signal: AbortSignal.timeout(12000) },
      );

      console.log(`[draft-sync] sync-down: query returned ${wraps.length} kind-1059 event(s) in ${Math.round(performance.now() - startedAt)}ms`);

      // Unwrap a few at a time rather than all at once (see
      // `mapWithConcurrency`). Most of these wraps are not drafts at all:
      // kind 1059 is shared with every NIP-17 DM client, and a relay can
      // only filter by the public `p` tag (the recipient), so telling a
      // draft from a DM requires attempting decryption — that part is
      // unavoidable. What IS avoidable is flooding the signer with all of
      // them concurrently.
      const outcomes = await mapWithConcurrency(wraps, 4, async (wrap) => {
        try {
          return { ok: true as const, draft: await unwrapDraftGiftWrap(wrap, user.signer) };
        } catch (error) {
          return { ok: false as const, error };
        }
      });

      const remote: ArticleDraft[] = [];
      let failed = 0;
      let aborted = 0;
      for (const outcome of outcomes) {
        if (outcome.ok) {
          // `undefined` means it decrypted fine but isn't a draft — an
          // ordinary DM. That's the common case, so it stays quiet.
          if (outcome.draft) {
            console.log(`[draft-sync] sync-down: unwrapped draft "${outcome.draft.title || outcome.draft.id}"`);
            remote.push(outcome.draft);
          }
        } else if (outcome.error instanceof Error && outcome.error.name === 'AbortError') {
          aborted += 1;
        } else {
          failed += 1;
        }
      }
      if (failed > 0) {
        // Expected noise, summarized rather than one log per wrap: DMs
        // encrypted under a different scheme, and any wraps published
        // before the re-signing fix, simply can't be unwrapped.
        console.log(`[draft-sync] sync-down: ${failed} wrap(s) not unwrapped (other clients' DMs or pre-fix wraps — expected).`);
      }
      if (aborted > 0) {
        // NOT expected: the signer itself timed these out. With
        // concurrency-limited unwraps this should be rare; if it recurs,
        // the remote signer is overloaded or offline.
        console.warn(`[draft-sync] sync-down: ${aborted} unwrap(s) aborted by signer timeout — drafts among these were skipped; try again.`);
      }

      console.log(`[draft-sync] sync-down: ${remote.length} draft(s) successfully unwrapped`);

      // One draft can have several wraps (one per sync-up). Keep only the
      // newest per draft id before merging — otherwise which revision
      // wins is decided by relay array order, not by time.
      const newestRemoteById = new Map<string, ArticleDraft>();
      for (const draft of remote) {
        const existing = newestRemoteById.get(draft.id);
        if (!existing || draft.updatedAt > existing.updatedAt) {
          newestRemoteById.set(draft.id, draft);
        }
      }

      const localById = new Map(drafts.drafts.map((d) => [d.id, d]));
      let pulled = 0;

      for (const draft of newestRemoteById.values()) {
        const local = localById.get(draft.id);
        // Newest revision wins across devices. Only write when the remote
        // copy is newer-or-equal — otherwise every re-sync would rewrite
        // identical rows and still claim "nothing pulled" when the
        // timestamp happened to match a previous sync.
        if (!local || draft.updatedAt >= local.updatedAt) {
          drafts.putSynced(draft);
          pulled += 1;
        }
      }

      console.log(`[draft-sync] sync-down: pulled ${pulled} draft(s), total ${Math.round(performance.now() - startedAt)}ms`);

      if (wraps.length === 0) {
        // Usually benign: relays take a moment to replicate, so a
        // sync-down immediately after a sync-up (or on a second device
        // whose relays differ) can come back empty. Worth a log line,
        // not an alarm.
        console.log(
          '[draft-sync] sync-down: no kind-1059 events found. If you just synced up, relay ' +
            'propagation can take a few seconds — try again. Otherwise check that read and write ' +
            'relays overlap (Network settings).',
        );
      } else if (remote.length === 0 && failed === 0 && aborted === 0) {
        // Every wrap decrypted but none were drafts — a healthy inbox of
        // ordinary DMs and nothing else. Quiet is correct here.
      } else if (remote.length === 0) {
        console.warn(
          `[draft-sync] sync-down: ${wraps.length} wrap(s) pulled, none were Wikistar drafts. ` +
            'If you expect drafts here, they may be pre-fix wraps (published re-signed, permanently ' +
            'undecryptable) — re-save them to sync fresh copies.',
        );
      }

      return pulled;
    } finally {
      setIsSyncing(false);
    }
  }, [user, nostr, drafts, config.relayMetadata.relays]);

  /**
   * Requests removal of a draft's synced copy.
   *
   * **This is best-effort and will usually not take effect**, which is a
   * property of NIP-59 rather than a bug here. NIP-09 says relays SHOULD
   * only honour a deletion whose `pubkey` is *identical* to the target
   * event's. A gift wrap is signed by a one-time ephemeral key that is
   * discarded immediately after sealing, so the user cannot produce a
   * deletion request that matches it — no conforming relay will drop the
   * wrap.
   *
   * The request is still sent (a relay may honour it via the `p` tag, and
   * it costs nothing), but the local record is cleared regardless so the
   * UI stops claiming the draft is synced. The caller must not present
   * this as a guaranteed remote delete.
   */
  const deleteSynced = useCallback(
    async (draftId: string): Promise<void> => {
      if (!user) return;
      const map = readSyncRecords();
      const record = map[draftId];
      if (!record) return;

      try {
        await publishEvent({
          kind: DELETION_KIND,
          content: '',
          tags: [['e', record.wrapId], ['k', String(GIFT_WRAP_KIND)]],
        });
      } catch (error) {
        // Never block the local delete on a request that was already
        // unlikely to be honoured.
        console.warn('[draft-sync] deletion request failed:', error);
      }

      delete map[draftId];
      writeSyncRecords(map);
    },
    [user, publishEvent],
  );

  /** Per-draft sync record lookup — used by the drafts page to badge each card. */
  const getSyncRecord = useCallback((draftId: string): SyncRecord | undefined => readSyncRecords()[draftId], []);

  return { isSyncing, syncDown, syncUp, syncAll, deleteSynced, canSync, getSyncRecord, writeRelays };
}
